import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mic, MicOff, Activity, ShieldCheck } from 'lucide-react';

//const SERVER_URL = "http://146.59.246.145:7860";
const SERVER_URL = window.location.origin; // Dynamique selon le domaine actuel

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [metrics, setMetrics] = useState({ ttfb: 0, processing: 0 });
  const [messages, setMessages] = useState([]);
  
  const pc = useRef(null);
  const localStream = useRef(null);
  const scrollRef = useRef(null);

  const startCall = async () => {
    // 1. RÉVEIL AUDIO (Critique pour Mobile)
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const audioCtx = new AudioContextClass();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
    }

    try {
      // 2. CAPTURE MICRO (Doit être la première action asynchrone après le clic)
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true } 
      });
      localStream.current = stream;

      // 3. SETUP WEBRTC
      pc.current = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });

      // 4. GESTION AUDIO DISTANT (Version compatible Mobile)
      pc.current.ontrack = (event) => {
        console.log("Flux audio du bot reçu !");
        let remoteAudio = document.getElementById("remote-audio-element");
        if (!remoteAudio) {
          remoteAudio = document.createElement("audio");
          remoteAudio.id = "remote-audio-element";
          remoteAudio.setAttribute("autoplay", "true");
          remoteAudio.setAttribute("playsinline", "true"); // OBLIGATOIRE POUR IOS
          document.body.appendChild(remoteAudio);
        }
        remoteAudio.srcObject = event.streams[0];
        remoteAudio.play().catch(e => console.error("Erreur lecture audio:", e));
      };

      pc.current.oniceconnectionstatechange = () => {
        console.log("État ICE:", pc.current.iceConnectionState);
        if (["connected", "completed"].includes(pc.current.iceConnectionState)) {
          setIsConnected(true);
        }
      };

      // 5. DATA CHANNEL UNIQUE (Gestion unifiée)
      const dc = pc.current.createDataChannel("pipecat");
      
      const handlePipecatMessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log("Data Pipecat:", data);

          if (data.type === "metrics" || data.metrics) {
            const m = data.metrics || data;
            setMetrics({
              ttfb: m.ttfb || m.processing_time || 0,
              processing: m.processing_time || 0
            });
          }

          if (data.type === "transcript" || data.type === "text") {
            setMessages(prev => [...prev, { 
              role: data.role || (data.type === "transcript" ? "bot" : "user"), 
              text: data.text 
            }]);
          }
        } catch (err) { console.error("Erreur Data:", err); }
      };

      dc.onmessage = handlePipecatMessage;
      // On écoute aussi si le serveur ouvre son propre canal
      pc.current.ondatachannel = (event) => { event.channel.onmessage = handlePipecatMessage; };

      // 6. AJOUT AUDIO LOCAL
      localStream.current.getTracks().forEach(track => {
        pc.current.addTrack(track, localStream.current);
      });

      // 7. NÉGOCIATION SDP
      const offer = await pc.current.createOffer();
      await pc.current.setLocalDescription(offer);

      // 8. SIGNALISATION (Utilisation de l'URL relative /api/offer)
      const response = await fetch(`${SERVER_URL}/api/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sdp: pc.current.localDescription.sdp,
          type: pc.current.localDescription.type
        })
      });

      if (!response.ok) throw new Error(`HTTP Error ${response.status}`);
      
      const answer = await response.json();
      await pc.current.setRemoteDescription(new RTCSessionDescription(answer));

      setMessages([{ role: 'system', text: 'Connexion établie...' }]);

    } catch (err) {
      console.error("Erreur d'appel:", err);
      alert(`Erreur: ${err.name}. Vérifiez le HTTPS et les permissions micro.`);
    }
  };

  const endCall = () => {
    pc.current?.close();
    localStream.current?.getTracks().forEach(t => t.stop());
    setIsConnected(false);
    setMessages([]);
    setMetrics({ ttfb: 0, processing: 0 });
  };

  const toggleMute = () => {
    if (localStream.current) {
      localStream.current.getAudioTracks()[0].enabled = isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-zinc-950 px-4">
      {/* Container Mobile-Style */}
      <div className="relative w-full max-w-md h-[800px] bg-black rounded-[3rem] border-[8px] border-zinc-800 shadow-2xl flex flex-col overflow-hidden">
        
        {/* Status Bar / Metrics */}
        <div className="pt-10 pb-4 px-8 flex justify-between items-end border-b border-white/5 bg-zinc-900/30">
          <div className="flex flex-col gap-1">
             <div className="flex items-center gap-2">
                <Activity size={12} className="text-orange-600" />
                <span className="text-[10px] font-mono text-zinc-400">TTFB: <b className="text-white">{metrics.ttfb.toFixed(0)}ms</b></span>
             </div>
             <div className="flex items-center gap-2">
                <ShieldCheck size={12} className="text-blue-500" />
                <span className="text-[10px] font-mono text-zinc-400">PROC: <b className="text-white">{metrics.processing.toFixed(0)}ms</b></span>
             </div>
          </div>
          <div className="text-right">
             <p className="text-[10px] font-bold text-orange-600 uppercase tracking-widest">Orange CI</p>
             <p className="text-[10px] text-zinc-500">Pascaline AI</p>
          </div>
        </div>

        {/* Transcript / Messages area */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6 space-y-4 scroll-smooth"
        >
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-4 rounded-[20px] text-sm leading-relaxed ${
                m.role === 'user' 
                ? 'bg-orange-600 text-white rounded-tr-none' 
                : 'bg-zinc-800 text-zinc-100 rounded-tl-none'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {/* Call Controls Area */}
        <div className="p-10 bg-gradient-to-t from-zinc-900 to-transparent">
          <div className="flex items-center justify-around">
            {/* Mute Button */}
            <button 
              onClick={toggleMute}
              disabled={!isConnected}
              className={`p-4 rounded-full transition-colors ${isMuted ? 'bg-red-500/20 text-red-500' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 disabled:opacity-20'}`}
            >
              {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
            </button>

            {/* Main Phone Button */}
            <button 
              onClick={isConnected ? endCall : startCall}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all transform active:scale-95 shadow-xl ${
                isConnected 
                ? 'bg-red-600 rotate-[135deg] shadow-red-600/30' 
                : 'bg-green-500 shadow-green-500/30 animate-pulse'
              }`}
            >
              <Phone size={32} fill="white" className="text-white" />
            </button>
            
            {/* Placeholder for future options */}
            <div className="w-14 h-14" /> 
          </div>
          
          {/* Bottom indicator (iOS style) */}
          <div className="mt-8 w-32 h-1.5 bg-zinc-800 mx-auto rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default App;
